import React, { forwardRef, Ref, useMemo } from 'react';

import Flex from '@gojob/ui-bits/Flex';
import ellipsis from 'polished/lib/mixins/ellipsis';

import { InputProps } from '@gojob/ui-bits/Input';
import useTheme from '@gojob/ui-bits/styles/useTheme';
import { transparentize } from 'polished';

const useStyles = () => {
  const { colors, spacing, fonts } = useTheme();
  return useMemo(
    () =>
      ({
        root: {
          minHeight: spacing(4),
          display: 'flex',
          flexDirection: 'column',
          ':after': {
            content: '""',
            display: 'block',
            height: 4,
            borderRadius: 3,
            backgroundColor: colors.anakiwa,
            transition: 'background-color 150ms ease-in-out',
          },
          ':focus-within::after, :hover::after': {
            backgroundColor: colors.white,
          },
        },
        input: {
          flex: 1,
          padding: 0,
          border: 'none',
          outline: 'none',
          width: '100%',
          height: spacing(5.5),
          backgroundColor: colors.transparent,
          ...fonts.headline3,
          lineHeight: 1.1,
          color: colors.secondary,
          caretColor: colors.secondary,
          ...ellipsis('100%'),
          '::placeholder': {
            ...fonts.headline3,
            fontWeight: 500,
            lineHeight: 1.1,
            color: transparentize(0.5, colors.inactive),
            fontSize: spacing(4.6),
          },
        },
        adornment: {
          position: 'relative',
          maxHeight: spacing(4),
          top: spacing(-1.5),
        },
      } as const),
    [colors, spacing, fonts]
  );
};

const Input = (props: InputProps<'input'>, ref: Ref<HTMLInputElement>) => {
  const {
    error,
    as: Element = 'input',
    wrapperRef,
    adornmentPosition,
    fullWidth,
    disabled,
    className,
    style,
    Adornment,
    ...other
  } = props;
  const classes = useStyles();

  return (
    <Flex ref={wrapperRef} css={classes.root} className={className} style={style}>
      <Flex flexDirection="row" alignItems="flex-end">
        <Element data-testid="Input" ref={ref} css={classes.input} disabled={disabled} {...other} />
        {Adornment ? <span css={classes.adornment}>{Adornment}</span> : null}
      </Flex>
    </Flex>
  );
};

export default forwardRef(Input);
